import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

const datasets = {
  monthly: [
    { label: "Messages Processed", value: 10.2, suffix: "M+" },
    { label: "Active Clients", value: 112, suffix: "+" },
    { label: "Hours Saved", value: 2500, suffix: "+" },
    { label: "Delivery Rate", value: 99.9, suffix: "%" },
  ],
  quarterly: [
    { label: "Messages Processed", value: 28.5, suffix: "M+" },
    { label: "Active Clients", value: 315, suffix: "+" },
    { label: "Hours Saved", value: 7200, suffix: "+" },
    { label: "Delivery Rate", value: 99.8, suffix: "%" },
  ],
  yearly: [
    { label: "Messages Processed", value: 120.4, suffix: "M+" },
    { label: "Active Clients", value: 1250, suffix: "+" },
    { label: "Hours Saved", value: 30000, suffix: "+" },
    { label: "Delivery Rate", value: 99.7, suffix: "%" },
  ],
};

const periodOptions = ["monthly", "quarterly", "yearly"];

const formatValue = (n, suffix) => {
  if (suffix === "%") {
    return `${n.toFixed(1)}${suffix}`;
  }
  if (suffix === "M+") {
    return `${n.toFixed(1)}${suffix}`;
  }
  return `${Math.round(n)}${suffix}`;
};

const ReviewStats = () => {
  const [period, setPeriod] = useState("monthly");
  const [displayValues, setDisplayValues] = useState([0, 0, 0, 0]);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const items = useMemo(() => datasets[period], [period]);

  useEffect(() => {
    if (!isInView) {
      return;
    }

    if (reduceMotion) {
      setDisplayValues(items.map((item) => item.value));
      return;
    }

    const nextValues = items.map((item) => item.value);
    const start = performance.now();
    const duration = 700;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setDisplayValues(nextValues.map((value) => value * progress));
      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }, [items, isInView, reduceMotion]);

  return (
    <section
      id="stats"
      ref={sectionRef}
      className="bg-gradient-to-b from-amber-50/30 to-white py-20 sm:py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-amber-700">
            Performance snapshot
          </p>
          <h2 className="mt-3 text-3xl font-black text-gray-900 sm:text-5xl">
            Measurable Growth, Clean Visibility
          </h2>
          <p className="mt-4 text-base text-gray-600 sm:text-lg">
            View impact by period and understand how your campaigns perform over
            time.
          </p>
        </motion.div>

        <div className="mx-auto mt-8 inline-flex w-full justify-center rounded-xl border border-amber-200 bg-white p-1 sm:w-auto">
          {periodOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setPeriod(option)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold capitalize transition-colors ${
                period === option
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                  : "text-gray-700 hover:text-amber-700"
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, idx) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: reduceMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.07 }}
              className="rounded-2xl border border-amber-100 bg-white p-6"
            >
              <div className="mb-4 inline-flex rounded-lg bg-amber-50 p-2 text-amber-700">
                <TrendingUp className="h-4 w-4" />
              </div>
              <p className="text-3xl font-black text-gray-900">
                {formatValue(displayValues[idx], item.suffix)}
              </p>
              <p className="mt-2 text-sm font-medium text-gray-600">{item.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-100/70 to-orange-100/50 p-6 text-center sm:flex-row sm:text-left">
          <p className="text-sm font-medium text-gray-700 sm:text-base">
            Want similar outcomes for your business communication flow?
          </p>
          <button
            type="button"
            onClick={() => navigate("/auth")}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white"
          >
            Get Started
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ReviewStats;